import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_ELSTIC_URL } from "../../config";
import { toast } from "react-toastify";

const ManageElasticSearch = () => {
  const [indexData, setIndexData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [syncingIndices, setSyncingIndices] = useState(new Set());
  const [syncAllLoading, setSyncAllLoading] = useState(false);

  // Mapping of index names to their sync API endpoints
  const syncEndpoints = {
    companies: "/sync/sync-company-data",
    rectification: "/sync/sync-rectification",
    correspondence: "/sync/sync-correspondence-data",
    oppositiondocuments: "/sync/sync-opposition-documents",
    journal: "/sync/sync-journal-data",
    opposition: "/sync/sync-opposition",
  };

  useEffect(() => {
    fetchElasticIndexData();
  }, []);

  const fetchElasticIndexData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(
        `${API_ELSTIC_URL}/companies/get-elastic-client-index-status`
      );
      setIndexData(response.data);
    } catch (error) {
      console.error("Error fetching index data:", error);
      setError("Failed to fetch index data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const syncIndex = async (indexName) => {
    const endpoint = syncEndpoints[indexName];
    if (!endpoint) {
      console.error(`No sync endpoint found for index: ${indexName}`);
      return;
    }

    try {
      setSyncingIndices((prev) => new Set(prev).add(indexName));

      const response = await axios.get(`${API_ELSTIC_URL}${endpoint}`);

      // Show success message or handle response
      //   console.log(`Sync completed for ${indexName}:`, response.data);
      toast.success(
        `Sync completed for ${indexName}:`,
        `Processed Count: ${response.data.result.processedCount}, Failed Count: ${response.data.result.failedCount}`
      );

      // Refresh the index data after successful sync
      await fetchElasticIndexData();
    } catch (error) {
      console.error(`Error syncing ${indexName}:`, error);
      // You can add toast notifications here
    } finally {
      setSyncingIndices((prev) => {
        const newSet = new Set(prev);
        newSet.delete(indexName);
        return newSet;
      });
    }
  };

  const syncAllIndices = async () => {
    try {
      setSyncAllLoading(true);

      // Sync all indices concurrently
      const syncPromises = Object.keys(syncEndpoints).map((indexName) =>
        syncIndex(indexName)
      );

      await Promise.all(syncPromises);
    } catch (error) {
      console.error("Error syncing all indices:", error);
    } finally {
      setSyncAllLoading(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "green":
        return "badge-success";
      case "yellow":
        return "badge-warning";
      case "red":
        return "badge-danger";
      default:
        return "badge-secondary";
    }
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat().format(num);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="row">
              <div className="col-12">
                <div
                  className="d-flex justify-content-center align-items-center"
                  style={{ height: "400px" }}
                >
                  <div className="spinner-border text-primary" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="row">
              <div className="col-12">
                <div className="alert alert-danger" role="alert">
                  <h4 className="alert-heading">Error!</h4>
                  <p>{error}</p>
                  <button
                    className="btn btn-outline-danger"
                    onClick={fetchElasticIndexData}
                  >
                    Try Again
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="page-content">
        <div className="container-fluid">
          {/* Page Header */}
          <div className="row">
            <div className="col-12">
              <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 className="mb-sm-0 font-size-18">Manage Elastic Search</h4>
                <div className="page-title-right">
                  <ol className="breadcrumb m-0">
                    <li className="breadcrumb-item">
                      <a href="javascript: void(0);">CMS</a>
                    </li>
                    <li className="breadcrumb-item active">
                      Manage Elastic Search
                    </li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          {/* Cluster Overview */}
          <div className="row">
            <div className="col-lg-12">
              <div className="card">
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6">
                      <h4 className="card-title mb-4">Cluster Overview</h4>
                    </div>
                    <div className="col-md-6 text-md-end">
                      <button
                        className="btn btn-primary me-2"
                        onClick={fetchElasticIndexData}
                        disabled={loading}
                      >
                        <i className="fas fa-sync-alt me-1"></i>
                        Refresh
                      </button>
                      <button
                        className="btn btn-success"
                        onClick={syncAllIndices}
                        disabled={syncAllLoading || syncingIndices.size > 0}
                      >
                        {syncAllLoading ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-1"
                              role="status"
                            ></span>
                            Syncing All...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-sync me-1"></i>
                            Sync All Indices
                          </>
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-3">
                      <div className="text-center">
                        <h5 className="text-muted">Cluster Status</h5>
                        <span
                          className={`badge badge-pill ${getStatusBadgeClass(
                            indexData?.cluster_status
                          )} fs-6`}
                        >
                          {indexData?.cluster_status?.toUpperCase() ||
                            "UNKNOWN"}
                        </span>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="text-center">
                        <h5 className="text-muted">Total Indices</h5>
                        <h3 className="text-primary">
                          {indexData?.total_indices || 0}
                        </h3>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="text-center">
                        <h5 className="text-muted">Total Documents</h5>
                        <h3 className="text-info">
                          {formatNumber(indexData?.total_documents || 0)}
                        </h3>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="text-center">
                        <h5 className="text-muted">Last Updated</h5>
                        <p className="text-muted mb-0">
                          {formatDate(new Date().toISOString())}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Indices Table */}
          <div className="row">
            <div className="col-lg-12">
              <div className="card">
                <div className="card-body">
                  <h4 className="card-title mb-4">Index Details</h4>

                  <div className="table-responsive">
                    <table className="table table-striped table-hover">
                      <thead className="table-dark">
                        <tr>
                          <th>Index Name</th>
                          <th>Document Count</th>
                          <th>Size</th>
                          <th>Created Date</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {indexData?.indices?.map((index, idx) => (
                          <tr key={idx}>
                            <td>
                              <div className="d-flex align-items-center">
                                <i className="fas fa-database text-primary me-2"></i>
                                <strong>{index.index_name}</strong>
                              </div>
                            </td>
                            <td>
                              <span className="badge badge-soft-info">
                                {formatNumber(index.document_count)}
                              </span>
                            </td>
                            <td>
                              <div>
                                <strong>{index.size_human}</strong>
                                <br />
                                <small className="text-muted">
                                  {formatNumber(index.size_bytes)} bytes
                                </small>
                              </div>
                            </td>
                            <td>
                              <small>{formatDate(index.created_date)}</small>
                            </td>
                            <td>
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => syncIndex(index.index_name)}
                                disabled={
                                  syncingIndices.has(index.index_name) ||
                                  syncAllLoading
                                }
                              >
                                {syncingIndices.has(index.index_name) ? (
                                  <>
                                    <span
                                      className="spinner-border spinner-border-sm me-1"
                                      role="status"
                                    ></span>
                                    Syncing...
                                  </>
                                ) : (
                                  <>
                                    <i className="fas fa-sync me-1"></i>
                                    Sync
                                  </>
                                )}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {(!indexData?.indices || indexData.indices.length === 0) && (
                    <div className="text-center py-4">
                      <i className="fas fa-database fa-3x text-muted mb-3"></i>
                      <h5 className="text-muted">No indices found</h5>
                      <p className="text-muted">
                        No Elasticsearch indices are currently available.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .badge-soft-info {
          color: #0f8adb;
          background-color: rgba(15, 138, 219, 0.1);
          padding: 0.375rem 0.75rem;
          border-radius: 0.375rem;
        }

        .badge-success {
          background-color: #28a745;
        }

        .badge-warning {
          background-color: #ffc107;
          color: #212529;
        }

        .badge-danger {
          background-color: #dc3545;
        }

        .badge-secondary {
          background-color: #6c757d;
        }

        .table-hover tbody tr:hover {
          background-color: rgba(0, 0, 0, 0.05);
        }

        .fs-6 {
          font-size: 1rem;
        }

        .spinner-border-sm {
          width: 1rem;
          height: 1rem;
        }

        .card {
          border: 1px solid #e9ecef;
          border-radius: 0.375rem;
          box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
        }

        .card-title {
          color: #495057;
          font-weight: 600;
        }

        .btn-outline-primary:disabled {
          opacity: 0.6;
        }

        .text-primary {
          color: #0d6efd !important;
        }

        .text-info {
          color: #0dcaf0 !important;
        }

        .text-muted {
          color: #6c757d !important;
        }
      `}</style>
    </div>
  );
};

export default ManageElasticSearch;
